/**
 * The log source named at viewer *invocation time* — the URL the page was opened
 * with: `?log_dir=` (a directory), `?log_file=` (a single file), or neither
 * (`none`). The two params are mutually exclusive intents, so this collapses them
 * into a discriminated union and throws on the contradictory combination.
 *
 * ⚠️ This is an INPUT, not configuration. It is one term in the calculus that
 * `resolveAppConfig()` (`app_config/appConfig.ts`) runs once at startup to resolve the
 * `AppConfig` (backend api, single-file mode, loader, and the resolved
 * `logFile`). It is useful ONLY up to that point: once `AppConfig` is resolved,
 * this must never be consulted again — read the resolved values off the config
 * (`getAppConfig()` / `useAppConfig()`) instead. To keep that true by
 * construction, `parseUrlLogSource` has exactly one caller: the resolver.
 */
export type UrlLogSource =
  | { kind: "dir"; logDir: string }
  | { kind: "file"; logFile: string }
  | { kind: "none" };

export const parseUrlLogSource = (search: string): UrlLogSource => {
  const params = new URLSearchParams(search);
  const logDir = params.get("log_dir");
  const logFile = params.get("log_file");
  if (logDir !== null && logFile !== null) {
    throw new Error(
      "?log_dir= and ?log_file= are mutually exclusive (a directory vs. a single file); pass only one."
    );
  }
  if (logFile !== null) return { kind: "file", logFile };
  if (logDir !== null) return { kind: "dir", logDir };
  return { kind: "none" };
};
